/**
 * Shared pricing & building features schema
 *
 * Used by all builder forms (BuildingForm, SingleRowForm, DoubleRowForm)
 * to collect optional pricing and feature data alongside template geometry.
 */

import { z } from 'zod'
import { PRICING_MODELS } from '@/types/hangars'
import type { PricingModel } from '@/types/hangars'

/** Zod schema for pricing + building features fields */
export const pricingFieldsSchema = z.object({
  // Pricing model
  pricing_model: z.enum(PRICING_MODELS as [PricingModel, ...PricingModel[]]).default('fixed'),

  // Fixed pricing (used when pricing_model='fixed')
  monthly_rate_private: z.coerce.number().min(0).nullable().optional(),
  monthly_rate_shared: z.coerce.number().min(0).nullable().optional(),
  transient_rate_nightly: z.coerce.number().min(0).nullable().optional(),

  // Per-sqft pricing (used when pricing_model='per_sqft')
  rate_per_sqft_private: z.coerce.number().min(0).nullable().optional(),
  rate_per_sqft_shared: z.coerce.number().min(0).nullable().optional(),

  // Building features
  construction_year: z.coerce
    .number()
    .min(1900, 'Year must be 1900 or later')
    .max(new Date().getFullYear() + 5, 'Year too far in the future')
    .nullable()
    .optional(),
  fire_suppression: z.boolean().default(false),
  security_system: z.boolean().default(false),
})

/** Inferred type for pricing form fields */
export type PricingFormFields = z.infer<typeof pricingFieldsSchema>

/** All pricing field keys (for extraction) */
const PRICING_KEYS = [
  'pricing_model',
  'monthly_rate_private',
  'monthly_rate_shared',
  'transient_rate_nightly',
  'rate_per_sqft_private',
  'rate_per_sqft_shared',
  'construction_year',
  'fire_suppression',
  'security_system',
] as const

/** Default pricing values for new buildings */
export const defaultPricingValues: PricingFormFields = {
  pricing_model: 'fixed',
  monthly_rate_private: undefined,
  monthly_rate_shared: undefined,
  transient_rate_nightly: undefined,
  rate_per_sqft_private: undefined,
  rate_per_sqft_shared: undefined,
  construction_year: undefined,
  fire_suppression: false,
  security_system: false,
}

/**
 * Extract pricing fields from a merged form data object.
 * Returns { pricing, rest } where rest contains all non-pricing fields.
 */
export function extractPricing<T extends Record<string, unknown>>(
  data: T
): { pricing: PricingFormFields; rest: Omit<T, (typeof PRICING_KEYS)[number]> } {
  const pricing: Record<string, unknown> = {}
  const rest: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(data)) {
    if ((PRICING_KEYS as readonly string[]).includes(key)) {
      pricing[key] = value
    } else {
      rest[key] = value
    }
  }

  return {
    pricing: pricing as PricingFormFields,
    rest: rest as Omit<T, (typeof PRICING_KEYS)[number]>,
  }
}
